import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

/* 투어 일정 정보 display section - used inside Card across:
   TourComplete, TourPayment
   departure: { address, time?, notes? }
   stopovers: [{ label, address }]
   arrival: { address } */
export default function TourScheduleSection({ departure, stopovers = [], arrival }) {
  const { t } = useTranslation();
  // 타임라인 행 — 아이콘 컬럼이 stretch 되어 박스 높이에 맞춰지고,
  // 위/아래 점선 spacer 가 아이콘을 박스 세로 중앙에 위치시키며 인접 행들과 연결.
  // first/last 플래그로 끝 단의 빈 spacer 처리 → 경유지 추가돼도 균일.
  const renderRow = ({ iconName, content, first = false, last = false, key }) => (
    <div key={key} className="flex gap-3 items-stretch">
      <div className="w-8 flex flex-col items-center shrink-0">
        <div
          className={
            first
              ? 'flex-1 min-h-[8px]'
              : 'flex-1 w-0 border-l-2 border-dashed border-text-muted min-h-[8px]'
          }
        />
        <div className="w-8 h-8 rounded-full bg-mono-50 flex items-center justify-center shrink-0">
          <Icon name={iconName} size={18} className="text-mono-500" />
        </div>
        <div
          className={
            last
              ? 'flex-1 min-h-[8px]'
              : 'flex-1 w-0 border-l-2 border-dashed border-text-muted min-h-[8px]'
          }
        />
      </div>
      <div className="flex-1 bg-mono-50 rounded-lg p-3.5 space-y-1">{content}</div>
    </div>
  );

  return (
    <>
      <h3 className="text-lg font-bold text-mono-900">{t('common.tour_schedule_info')}</h3>

      {/* Day header */}
      <div className="flex items-center gap-2">
        <div className="w-1 h-5 bg-orange-500 rounded-full" />
        <h3 className="text-base font-bold text-mono-900">{t('common.day1')}</h3>
      </div>

      <div className="flex flex-col">
        {renderRow({
          iconName: 'van',
          first: true,
          content: (
            <>
              <span className="text-2sm font-medium text-mono-500">{t('common.departure_label')}</span>
              <p className="text-sm font-bold text-mono-900">{departure.name || departure.address}</p>
              {departure.name && departure.address && departure.name !== departure.address && (
                <p className="text-2sm text-mono-500">{departure.address}</p>
              )}
              {departure.time && (
                <div className="flex items-center gap-1.5 mt-1">
                  <Icon name="clock" size={14} className="text-mono-400" />
                  <span className="text-2sm text-mono-500">{departure.time}</span>
                </div>
              )}
              {departure.notes && (
                <span className="text-2sm font-normal text-mono-500">{departure.notes}</span>
              )}
            </>
          ),
        })}

        {stopovers.map((stop, i) => renderRow({
          key: `stop-${i}`,
          iconName: 'place',
          content: (
            <>
              <span className="text-2sm font-medium text-mono-500">{stop.label}</span>
              <p className="text-sm font-bold text-mono-900">{stop.name || stop.address}</p>
              {stop.name && stop.address && stop.name !== stop.address && (
                <p className="text-2sm text-mono-500">{stop.address}</p>
              )}
            </>
          ),
        }))}

        {renderRow({
          iconName: 'place',
          last: true,
          content: (
            <>
              <span className="text-2sm font-medium text-mono-500">{t('common.destination_label')}</span>
              <p className="text-sm font-bold text-mono-900">{arrival.name || arrival.address}</p>
              {arrival.name && arrival.address && arrival.name !== arrival.address && (
                <p className="text-2sm text-mono-500">{arrival.address}</p>
              )}
            </>
          ),
        })}
      </div>
    </>
  );
}
